import { getTimeSign } from '@/api/public';
import { GiftDialogFlow, GiftSingleDialog } from '@/components/GiftDetailsDialog';
import type { CartShowGiftDto } from '@/api/types/gift';
import type { CartSummaryGiftPresentation } from '@/domain/cart/gift-exposure';
import { useCartGiftTracking } from '@/domain/cart/gift-tracking/client';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { colors, font } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Badge } from '@mui/material';
import { ShoppingBagOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import GiftPopover from './GiftPopover';
import HeaderHoverPopover from './HeaderHoverPopover';
import { getCartSidebarPresentation } from './cartSidebarPresentation';
import { useWhenCurReady } from '@/hooks/useWhenReady';
const CartPopover = dynamic(() => import('./CartPopover'), { ssr: false });

export default function CartIcon({
  isDarkMode,
  cartProductNum,
  deviceType,
  icon,
}: {
  isDarkMode: boolean;
  cartProductNum: number | string;
  deviceType: EPlatform;
  icon?: ReactNode;
}) {
  const { trackEvent } = useReporting();
  const { pathname, cnName } = useRouteMetadata();
  const router = useRouter();
  const giftCount = useStore((state) => state.giftCount);
  const [showCartPopover, setShowCartPopover] = useState(false);
  const [, setCountDownTime] = useState(5);
  const showGiftPopoverTimer = useRef<NodeJS.Timeout | null>(null);
  const setCart = useStore((state) => state.setCart);
  const cartTotalPrice = useStore((state) => state.cartTotalPrice);
  const token = useStore((state) => state.token);
  const tokenReady = useStore((state) => state.clientTokenReady);
  const userReady = useStore((state) => state.userInfoReady);
  const userId = useStore((state) => state.userInfo?.id);
  const isMobile = deviceType !== EPlatform.WEB;
  const cartExperiment = useStore((state) => state.cartExperiment);
  const presentation = getCartSidebarPresentation(pathname, isMobile, cartExperiment);
  const isV1Sidebar = presentation === 'v1';
  const [sidebarTop, setSidebarTop] = useState(0);
  const [sidebarRevision, setSidebarRevision] = useState(0);
  // 全局组件 B 位固定 global_pc/global_wap，与所在页面无关。
  const spmPage = useGlobalSpmTerminal(deviceType);
  // Data SPM DOM 打标：与 handleCartClick 上报的 cart_entry 坑位同源同值。
  const cartEntryDomSpm = useSpmDomAttr(getSpmPositionCode(spmPage, 'header_tools', 'cart_entry'));
  const [cartPopoverShow, setCartPopoverShow] = useState(false);
  const [showTimer, setShowTimer] = useState<NodeJS.Timeout | null>(null);
  const [cartGiftDialog, setCartGiftDialog] = useState<CartSummaryGiftPresentation | null>(null);
  const [cartGiftShowGift, setCartGiftShowGift] = useState<CartShowGiftDto | null>(null);
  const [cartGiftBaseSubtotal, setCartGiftBaseSubtotal] = useState<string | number | undefined>();
  const currency = useStore((state) => state.currency);
  const giftTracking = useCartGiftTracking({
    showGift: cartGiftShowGift || undefined,
    baseSubtotal: cartGiftBaseSubtotal,
    currency: currency?.name || '',
    open: Boolean(cartGiftDialog),
  });
  // Store state
  const canBuyCartsList = useStore((state) => state.canBuyCartsList);
  const giftPopoverShowInterval = useRef<NodeJS.Timeout | null>(null);
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);
  const tA11y = useTranslations('layout.header.a11y');
  const giftT = useTranslations('cart.GiftsWithCartItem');

  // 图标颜色类名
  const iconColorClass = isDarkMode ? 'text-white' : 'text-primary-black';
  const cartIcon = icon ?? (
    <ShoppingBagOutlined className={`cursor-pointer text-[24px] ${iconColorClass}`} aria-hidden />
  );

  // Badge公共样式
  const commonBadgeStyles = {
    position: 'absolute',
    boxSizing: 'border-box',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: colors.primaryPurple,
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: font.br,
    display: 'flex',
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'auto',
    '& .MuiBadge-badge.MuiBadge-standard.MuiBadge-invisible': {
      display: 'none',
    },
  };

  // Badge样式配置 - 根据设备类型选择特定样式
  const deviceSpecificStyles = isMobile
    ? {
        // 移动端特定样式
        top: '-1.067vw',
        right: '-4px',
        minWidth: '3.733vw',
        height: '3.733vw',
        lineHeight: '3.733vw',
        padding: '0 1.067vw',
        borderRadius: '2.133vw',
        fontSize: '2.933vw',
      }
    : {
        // PC端特定样式
        top: '-7px',
        left: '40px',
        minWidth: '16px',
        height: '16px',
        lineHeight: '16px',
        padding: '0 4px',
        borderRadius: '8px',
        fontSize: '12px',
      };

  // 合并公共样式和设备特定样式
  const badgeStyles = {
    ...commonBadgeStyles,
    ...deviceSpecificStyles,
  };

  useWhenCurReady(() => {
    // 初始化购物车的数据
    setCart();

    return () => {
      if (showGiftPopoverTimer.current) {
        clearInterval(showGiftPopoverTimer.current);
      }
    };
  });

  // 账户慢于购物车返回时补算；身份变化也必须使已成功报价失效。
  useWhenCurReady(() => {
    if (!isMobile) void cartTotalPrice();
  }, [cartTotalPrice, isMobile, token, tokenReady, userReady, userId, currency?.name]);

  const handleCartClick = () => {
    const cartEntrySpmCnt = getSpmPositionCode(spmPage, 'header_tools', 'cart_entry');
    const properties = {
      tab_name: '购物车',
      url_path: window.location.href,
      page_name: cnName, // 使用路由元数据中的中文名称
      tab_index: 4,
    };
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: cartEntrySpmCnt ? withSpmCnt(properties, cartEntrySpmCnt) : properties,
      },
      [ProviderType.SENSORS],
    );
    sessionStorage.setItem('zl-sensors-entrance-cart-scene', '导航位');
    if (pathname === '/cart') {
      return;
    }
    router.push('/cart');
  };

  const judgeGiftPopoverShow = useCallback(() => {
    const flag1 = giftCount > 0; // 赠品的数量大于0
    const flag2 = !['/goods-detail', '/goods-list', '/cart'].some((path) =>
      location.pathname?.startsWith(path),
    );
    if (flag1 && flag2) {
      setShowCartPopover(true);
    }
  }, [giftCount]);

  useEffect(() => {
    judgeGiftPopoverShow();
    giftPopoverShowInterval.current && clearInterval(giftPopoverShowInterval.current);
    giftPopoverShowInterval.current = setInterval(
      () => {
        judgeGiftPopoverShow();
      },
      15 * 60 * 1000,
    );
  }, [giftCount]);

  // 监听礼品弹窗状态变化
  useEffect(() => {
    if (showCartPopover) {
      if (showGiftPopoverTimer.current) {
        clearInterval(showGiftPopoverTimer.current);
      }
      showGiftPopoverTimer.current = setInterval(() => {
        setCountDownTime((prev) => {
          if (prev <= 1) {
            setShowCartPopover(false);
            return 5;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountDownTime(5);
      if (showGiftPopoverTimer.current) {
        clearInterval(showGiftPopoverTimer.current);
      }
    }
  }, [showCartPopover]);

  const getServerTimeSign = async () => {
    const { data: { exist } = {} } = await getTimeSign({
      type: 'todayCartView',
      second: 3,
    });
    return exist;
  };
  // Watch cartDown prop
  useEffect(() => {
    if (cartPopoverShow) {
      // 弹层弹出3s，视为访问过购物车页
      const timer = setTimeout(() => {
        getServerTimeSign();
      }, 3000);
      setShowTimer(timer);
      trackEvent(
        {
          eventName: 'CartPopUp_Exp',
          properties: {
            page_name: cnName,
            url_path: window.location.href,
          },
        },
        [ProviderType.SENSORS],
      );
    } else if (showTimer) {
      clearTimeout(showTimer);
      setShowTimer(null);
    }

    return () => {
      if (showTimer) {
        clearTimeout(showTimer);
      }
    };
  }, [cartPopoverShow]);

  const cartButtonClass = isMobile
    ? 'm-0 flex h-full w-full cursor-pointer items-center justify-center border-0 bg-transparent p-0'
    : 'flex justify-center items-center p-0 m-0 border-0 bg-transparent cursor-pointer pb-[14px] -mb-[14px] pl-[24px]';

  return (
    <>
      <li
        className={isMobile ? 'relative flex shrink-0 items-center justify-center' : 'relative'}
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          ...(isMobile ? { width: 24, height: 24, flexShrink: 0 } : {}),
        }}
        onClick={handleCartClick}
      >
        {pathname === '/cart' || isMobile || canBuyCartsList.length === 0 ? (
          <button
            type="button"
            aria-label={tA11y('cart')}
            onClick={(e) => {
              e.stopPropagation();
              handleCartClick();
            }}
            className={cartButtonClass}
            {...cartEntryDomSpm}
          >
            {cartIcon}
          </button>
        ) : (
          <HeaderHoverPopover
            key={`${presentation}-${sidebarRevision}`}
            id="cart-popover"
            onOpenChange={setCartPreviewOpen}
            onOpen={
              isV1Sidebar
                ? () =>
                    setSidebarTop(
                      document.getElementById('header')?.getBoundingClientRect().bottom || 0,
                    )
                : undefined
            }
            triggerComponent={
              <button
                type="button"
                aria-label={tA11y('openCartPreview')}
                aria-expanded={cartPreviewOpen}
                aria-haspopup="dialog"
                className={cartButtonClass}
                onClick={handleCartClick}
                {...cartEntryDomSpm}
              >
                {cartIcon}
              </button>
            }
            content={
              <CartPopover
                presentation={presentation}
                onViewCart={
                  isV1Sidebar
                    ? () => {
                        setCartPreviewOpen(false);
                        setCartPopoverShow(false);
                        setSidebarRevision((revision) => revision + 1);
                      }
                    : undefined
                }
                onGiftSummaryClick={(gift, showGift, baseSubtotal) => {
                  setCartGiftShowGift(showGift);
                  setCartGiftBaseSubtotal(baseSubtotal);
                  setCartGiftDialog(gift);
                }}
              />
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            width={isV1Sidebar ? 'min(608px, 100vw)' : '24.062vw'}
            {...(isV1Sidebar
              ? {
                  anchorReference: 'none' as const,
                  marginThreshold: 0,
                  sx: {
                    '& .MuiBackdrop-root': {
                      top: sidebarTop,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      backdropFilter: 'blur(15px)',
                    },
                    '& .MuiPopover-paper': {
                      top: sidebarTop,
                      right: 0,
                      left: 'auto',
                      bottom: 0,
                      margin: 0,
                      maxHeight: 'none',
                      maxWidth: '100vw',
                      borderRadius: 0,
                    },
                    '& .popover-content': { height: '100%' },
                  },
                }
              : {})}
            noPadding
            slotProps={{
              transition: {
                onEnter: () => {
                  void cartTotalPrice();
                  setCartPopoverShow(true);
                },
                onExited: () => {
                  setCartPopoverShow(false);
                },
              },
            }}
          />
        )}
        {!!cartProductNum && (
          <Badge sx={badgeStyles}>
            <span
              style={{
                ...(cartProductNum === '...' && { transform: 'translateY(-20%)' }),
              }}
            >
              {cartProductNum}
            </span>
          </Badge>
        )}
        <GiftPopover show={showCartPopover} giftCount={giftCount} />
      </li>
      {cartGiftDialog?.dialogType === 'tier-flow' && cartGiftDialog.dialogActivities?.length ? (
        <GiftDialogFlow
          open={Boolean(cartGiftDialog)}
          onClose={() => {
            setCartGiftDialog(null);
            setCartGiftShowGift(null);
            setCartGiftBaseSubtotal(undefined);
          }}
          title={cartGiftDialog.dialogTitle || giftT('giftList')}
          activities={cartGiftDialog.dialogActivities}
        />
      ) : null}
      {cartGiftDialog?.dialogType === 'single-gift' && cartGiftDialog.singleDialogGift ? (
        <GiftSingleDialog
          open={Boolean(cartGiftDialog)}
          title={cartGiftDialog.dialogTitle || giftT('giftList')}
          gift={cartGiftDialog.singleDialogGift}
          onClose={() => {
            setCartGiftDialog(null);
            setCartGiftShowGift(null);
            setCartGiftBaseSubtotal(undefined);
          }}
        />
      ) : null}
    </>
  );
}
